import { select } from 'd3-selection';
import deepmerge from 'deepmerge';
import { EventSource, CodeObjectType } from '@appland/models';
import { zoom, zoomIdentity, zoomTransform } from 'd3-zoom';
import { interpolate } from 'd3-interpolate';
import dagre from 'dagre';
import { line, curveBasis } from 'd3-shape';

class Transform {
  constructor(x = 0, y = 0, k = 1) {
    this.x = x;
    this.y = y;
    this.k = k;
  }

  toString() {
    return [
      this.x !== 0 ? `translateX(${this.x}px)` : null,
      this.y !== 0 ? `translateY(${this.y}px)` : null,
      this.k !== 1 ? `scale(${this.k}` : null,
    ].join(' ');
  }
}

var Geometry = {
  // Checks if a rect (such as a DOMRect) fully contains another.
  contains(outer, inner) {
    return (
      outer.top <= inner.top &&
      outer.bottom >= inner.bottom &&
      outer.left <= inner.left &&
      outer.right >= inner.right
    );
  },

  inContainer(outer, inner) {
    const x = inner.x - outer.x;
    const y = inner.y - outer.y;

    return x > 0 && y > 0 && x + inner.width < outer.width && y + inner.height < outer.height;
  },

  // delta returns the shortest relative translation to place inner within
  // outer
  delta(outer, inner) {
    let x = 0;
    let y = 0;

    if (outer.left >= inner.left) {
      x = outer.left - inner.left;
    } else if (outer.right <= inner.right) {
      x = outer.right - inner.right;
    }

    if (outer.top >= inner.top) {
      y = outer.top - inner.top;
    } else if (outer.bottom <= inner.bottom) {
      y = outer.bottom - inner.bottom;
    }

    return { x, y };
  },

  // calculates a transform that shifts from one position to another
  shift(from, to) {
    return new Transform(from.x - to.x, from.y - to.y);
  },

  Transform,
};

function getEventTarget(target, container = document, selector = false) {
  let el = target;
  let selectedElements;

  if (selector) {
    selectedElements = Array.from(container.querySelectorAll(selector));
  } else {
    selectedElements = [container];
  }

  while (el) {
    if (selectedElements.includes(el)) {
      break;
    }

    el = el.parentNode;
  }

  return el;
}

// Move the minimum amount to put the element into view
function lazyPanToElement(viewport, element, padding = 0) {
  if (!element) {
    return;
  }

  let { x, y } = Geometry.delta(
    viewport.element.getBoundingClientRect(),
    element.getBoundingClientRect()
  );

  // Apply padding
  x += Math.sign(x) * padding;
  y += Math.sign(y) * padding;

  // Scale the offset using the current transform. This is necessary to put the
  // element in view at different scales.
  const { k } = viewport.transform;
  x /= k;
  y /= k;

  viewport.translateBy(x, y);
}

function nodeFullyVisible(container, node) {
  if (!node) return false;
  return Geometry.inContainer(container.getBoundingClientRect(), node.getBoundingClientRect());
}

function getParentRelativeOffset(element, parent) {
  const offset = {
    left: 0,
    top: 0,
  };

  let child = element;
  while (child && child !== parent) {
    offset.left += child.offsetLeft;
    offset.top += child.offsetTop;
    child = child.offsetParent;
  }

  return offset;
}

function getElementCenter(node) {
  return {
    x: node.offsetLeft + node.clientWidth * 0.5,
    y: node.offsetTop + node.clientHeight * 0.5,
  };
}

const STALE_TIME = 0.63; // seconds

function removeStaleSamples(accumulator) {
  let lastTime = Date.now();
  for (let i = accumulator.values.length - 1; i >= 0; i -= 1) {
    const sample = accumulator.values[i];
    const dt = (lastTime - sample.time) / 60.0;

    // If enough time has passed between the new sample and the last sample,
    // all the existing data is considered stale and removed.
    if (dt > accumulator.staleTime) {
      accumulator.values.splice(0, accumulator.values.length);
      break;
    }

    lastTime = sample.time;
  }
}

// Accumulator keeps a running average of values up to a max number of samples
// samples decay and are removed after the staleTime
class Accumulator {
  constructor(maxSamples, staleTime = STALE_TIME) {
    this.maxSamples = maxSamples;
    this.staleTime = staleTime;
    this.reset();
  }

  add(value) {
    removeStaleSamples(this);

    if (this.values.length >= this.maxSamples) {
      this.values.splice(0, this.values.length - this.maxSamples + 1);
    }

    this.values.push({ value, time: Date.now() });
  }

  reset() {
    this.values = [];
  }

  get length() {
    return this.values.length;
  }

  get value() {
    removeStaleSamples(this);
    const length = this.values.length - 1;
    /* eslint-disable no-param-reassign, no-return-assign */
    return length !== 0
      ? this.values.reduce((acc, sample) => (acc += sample.value), 0) / length
      : 0;
    /* eslint-enable no-param-reassign, no-return-assign */
  }
}

const FRICTION_COEFFICIENT = 2.5;
const IMPULSE_THRESHOLD = 1;
const EPSILON = 0.2;
const SAMPLES = 8;

class Momentum {
  constructor(zoom, selection) {
    this.vX = new Accumulator(SAMPLES);
    this.vY = new Accumulator(SAMPLES);
    this.transform = { x: 0.0, y: 0.0, k: 0.0 };
    this.velocity = { x: 0, y: 0 };
    this.selection = selection;
    this.node = selection.node();
    this.zoom = zoom;
    this.active = false;
    this.ticking = false;
  }

  cancel() {
    if (this.lastTick) {
      delete this.lastTick;
    }

    this.active = false;
    this.ticking = false;
  }

  hold() {
    this.active = false;
    this.holding = true;
    this.vX.reset();
    this.vY.reset();
  }

  release() {
    if (!this.holding) {
      return;
    }

    this.holding = false;
    this.impulse(this.vX.value, this.vY.value);
  }

  impulse(x, y, threshold = IMPULSE_THRESHOLD) {
    if (Math.abs(x) + Math.abs(y) < threshold) {
      return;
    }

    this.velocity.x = x / this.transform.k;
    this.velocity.y = y / this.transform.k;

    if (this.ticking) {
      return;
    }

    this.active = true;
    this.tick();
  }

  tick() {
    if (!this.moving || !this.active) {
      this.cancel();
      return;
    }

    this.ticking = true;

    requestAnimationFrame((t) => {
      if (!this.active) {
        this.cancel();
        return;
      }

      if (!this.lastTick) {
        this.lastTick = t - 1;
      }

      const dt = (t - this.lastTick) / 1000.0;

      this.zoom.translateBy(this.selection, this.velocity.x, this.velocity.y);

      this.velocity.x -= this.velocity.x * FRICTION_COEFFICIENT * dt;
      this.velocity.y -= this.velocity.y * FRICTION_COEFFICIENT * dt;

      this.lastTick = t;
      this.tick();
    });
  }

  updateTransform(transform) {
    // check if translation has changed by checking if scale hasn't
    if (!this.active && transform.k === this.transform.k) {
      this.vX.add(transform.x - this.transform.x);
      this.vY.add(transform.y - this.transform.y);
    }

    this.transform.x = transform.x;
    this.transform.y = transform.y;
    this.transform.k = transform.k;
  }

  get moving() {
    return Math.abs(this.velocity.x) + Math.abs(this.velocity.y) > EPSILON;
  }
}

function momentum(zoom, selection) {
  /* eslint-disable no-param-reassign */
  const m = new Momentum(zoom, selection);
  const onZoom = zoom.on('zoom');
  const onBlur = window.onblur;

  selection
    .on('mousedown', () => m.hold())
    .on('mouseup', () => m.release())
    .on('pointerdown', () => m.hold())
    .on('pointerup', () => m.release())
    .on('touchstart', () => m.hold())
    .on('touchend', () => m.release())
    .on('touchcancel', () => m.release());

  window.addEventListener('mouseup', () => m.release());
  window.addEventListener('pointerup', () => m.release());
  window.onblur = () => {
    if (onBlur) {
      onBlur();
    }

    m.cancel();
  };

  zoom.on('zoom', (d3Event) => {
    if (!d3Event) {
      return;
    }

    m.updateTransform(d3Event.transform);
    if (onZoom) {
      onZoom(d3Event);
    }
  });

  const { translateTo } = zoom;
  zoom.translateTo = (...args) => {
    m.cancel();
    return translateTo(...args);
  };

  const { translate } = zoom;
  zoom.translate = (...args) => {
    m.cancel();
    return translate(...args);
  };

  return zoom;
  /* eslint-enable no-param-reassign */
}

// updateZoom updates the bar that indicates the current level of zoom.
// `zoomScale` is a float, ranging from 0.0 (fully zoomed in) to 1.0 (fully
// zoomed out)
function updateZoom(viewportZoom, zoomScale) {
  /* eslint-disable no-param-reassign */
  const { controls } = viewportZoom;
  if (!viewportZoom.maxZoomBarValue) {
    const zoomBarHeight = controls.zoomBar.getBoundingClientRect().height;
    const zoomGrabHeight = controls.zoomGrab.getBoundingClientRect().height;
    viewportZoom.maxZoomBarValue = zoomBarHeight - zoomGrabHeight;
  }

  const { maxZoomBarValue } = viewportZoom;
  const topOffset = maxZoomBarValue - maxZoomBarValue * zoomScale;
  viewportZoom.zoomScale = zoomScale;

  controls.zoomGrab.style.top = `${topOffset}px`;
  /* eslint-enable no-param-reassign */
}

/* eslint-disable no-param-reassign */
function createDOM(viewportZoom) {
  const controlsContainer = document.createElement('div');
  controlsContainer.className = 'appmap__zoom';

  const { controls } = viewportZoom;
  controls.buttonZoomIn = document.createElement('button');
  controls.buttonZoomIn.setAttribute('type', 'button');
  controls.buttonZoomIn.className = 'appmap__zoom-button';
  controls.buttonZoomIn.innerHTML = '&plus;';
  controlsContainer.appendChild(controls.buttonZoomIn);

  controls.zoomBar = document.createElement('div');
  controls.zoomBar.className = 'appmap__zoom-bar';
  controls.zoomGrab = document.createElement('div');
  controls.zoomGrab.className = 'appmap__zoom-grab';
  controls.zoomBar.appendChild(controls.zoomGrab);
  controlsContainer.appendChild(controls.zoomBar);

  controls.buttonZoomOut = document.createElement('button');
  controls.buttonZoomOut.setAttribute('type', 'button');
  controls.buttonZoomOut.className = 'appmap__zoom-button';
  controls.buttonZoomOut.innerHTML = '&minus;';
  controlsContainer.appendChild(controls.buttonZoomOut);

  viewportZoom.container.appendChild(controlsContainer);
  viewportZoom.element = controlsContainer;

  controls.buttonZoomIn.addEventListener('click', (event) => {
    event.stopPropagation();

    viewportZoom.zoomScale = Math.min(1.0, viewportZoom.zoomScale + viewportZoom.step);
    viewportZoom.emit('zoom', viewportZoom.zoomScale);
  });

  controls.buttonZoomOut.addEventListener('click', (event) => {
    event.stopPropagation();

    viewportZoom.zoomScale = Math.max(0.0, viewportZoom.zoomScale - viewportZoom.step);
    viewportZoom.emit('zoom', viewportZoom.zoomScale);
  });

  controls.zoomBar.addEventListener('click', (event) => {
    event.stopPropagation();

    if (event.target !== controls.zoomBar) {
      return false;
    }

    const maxOffset = controls.zoomBar.getBoundingClientRect().height;
    const offset = event.clientY - Math.round(controls.zoomBar.getBoundingClientRect().top);

    viewportZoom.emit('zoom', 1.0 - offset / maxOffset);

    return true;
  });

  controls.zoomBar.addEventListener('mousedown', (event) => {
    document.body.style.cursor = 'grabbing';
    viewportZoom.zoomGrabPosition = controls.zoomGrab.offsetTop;
    viewportZoom.dragStart = event.clientY;
    viewportZoom.isDragging = true;
    event.stopPropagation();
  });

  document.body.addEventListener('mousemove', (event) => {
    if (viewportZoom.isDragging) {
      const maxOffset = controls.zoomBar.getBoundingClientRect().height;
      const offset = viewportZoom.zoomGrabPosition + (event.clientY - viewportZoom.dragStart);
      viewportZoom.emit('zoom', 1.0 - offset / maxOffset);
      event.preventDefault();
    }
  });

  document.body.addEventListener('mouseup', () => {
    document.body.style.cursor = null;
    viewportZoom.isDragging = false;
  });
}
/* eslint-enable no-param-reassign */

class ContainerZoom extends EventSource {
  constructor(container, options) {
    super();

    this.container = container.element;
    this.step = options.step;
    this.controls = {};
    this.maxZoomBarValue = null;
    this.dragStart = null;
    this.zoomGrabPosition = null;
    this.isDragging = false;
    this.zoomGrabTimeout = null;

    createDOM(this);
    updateZoom(this, container.transform.k);
    container.on('move', (transform) =>
      updateZoom(this, (transform.k - options.minRatio) / (options.maxRatio - options.minRatio))
    );
  }
}

function transformElement$1(item, element) {
  if (typeof item.data.transform === 'function') {
    return item.data.transform(element);
  }
  return element;
}

class ContextMenuItem extends EventSource {
  constructor() {
    super();
    this.data = {
      text: 'Untitled item',
      selector: null,
      condition: null,
      transform: null,
    };
  }

  text(value) {
    this.data.text = value;
    return this;
  }

  selector(value) {
    this.data.selector = value;
    return this;
  }

  condition(fn) {
    this.data.condition = fn;
    return this;
  }

  transform(fn) {
    this.data.transform = fn;
    return this;
  }

  match(e) {
    const matchSelector = !this.data.selector || e.matches(this.data.selector);
    if (!matchSelector) {
      return false;
    }

    const subject = transformElement$1(this, e);
    if (!subject) {
      // we have a transform and it failed to resolve
      return false;
    }

    const matchCondition = !this.data.condition || this.data.condition(subject);
    return matchCondition;
  }
}

function initializeDomElements(parent) {
  const dropdown = document.createElement('div');
  dropdown.classList.add('appmap__context-menu');
  dropdown.style.display = 'none';

  const dropdownMenu = document.createElement('div');
  dropdownMenu.classList.add('dropdown-menu');
  dropdown.appendChild(dropdownMenu);

  // Don't propagate mousedown events to elements we're above. For example, we
  // don't want the user being able to pan a viewport through this element.
  // OTOH, maybe this shouldn't be a class behavior and should be handled by the
  // context menu owner. If this causes issues in the future we can move this
  // out.
  dropdown.addEventListener('mousedown', (e) => e.stopPropagation());
  dropdown.addEventListener('pointerdown', (e) => e.stopPropagation());
  dropdown.addEventListener('touchstart', (e) => e.stopPropagation());

  const emptyMessage = document.createElement('p');
  emptyMessage.innerText = 'No actions available';
  emptyMessage.style.display = 'none';
  dropdownMenu.appendChild(emptyMessage);

  parent.appendChild(dropdown);

  return {
    dropdown,
    menu: dropdownMenu,
    emptyMessage,
  };
}

function addItem(contextMenu, item) {
  /* eslint-disable no-param-reassign */
  const itemElement = document.createElement('a');
  itemElement.classList.add('dropdown-item');
  itemElement.innerText = item.data.text;
  contextMenu.elements.menu.appendChild(itemElement);
  item.element = itemElement;

  return itemElement;
  /* eslint-enable no-param-reassign */
}

function addDivider(contextMenu) {
  const divider = document.createElement('div');
  divider.classList.add('dropdown-divider');
  contextMenu.elements.menu.appendChild(divider);
}

function transformElement(item, element) {
  if (typeof item.data.transform === 'function') {
    return item.data.transform(element);
  }
  return element;
}

function show(contextMenu, clickEvent) {
  /* eslint-disable no-param-reassign */
  let itemsDisplayed = 0;

  // Remove ancestors of the container element, we don't need to iterate any
  // further than that.
  const activeAreaIndex = clickEvent.composedPath().findIndex((e) => e === contextMenu.activeArea);

  if (activeAreaIndex === -1) {
    return;
  }

  const path = clickEvent.composedPath().slice(0, activeAreaIndex + 1);

  contextMenu.items.forEach((item) => {
    const match = path.find((e) => item.match(e));
    if (!match) {
      item.element.style.display = 'none';
      return;
    }

    if (item.element.listener) {
      // make sure there's no old state
      item.element.removeEventListener('click', item.element.listener);
    }

    item.element.listener = () => item.emit('execute', transformElement(item, match));
    item.element.addEventListener('click', item.element.listener);
    item.element.style.display = '';
    item.emit('show');
    itemsDisplayed += 1;
  });

  contextMenu.elements.emptyMessage.style.display = itemsDisplayed > 0 ? 'none' : '';

  const { x, y } = contextMenu.parent.getBoundingClientRect();
  contextMenu.elements.menu.style.transform = `translate(${clickEvent.x - x}px, ${
    clickEvent.y - y
  }px)`;
  contextMenu.elements.dropdown.style.display = 'block';
  /* eslint-enable no-param-reassign */
}

class ContextMenu extends EventSource {
  constructor(container, activeArea = null) {
    super();

    this.parent = container;
    this.activeArea = activeArea || container;
    this.elements = initializeDomElements(container);
    this.activeArea.addEventListener('contextmenu', (e) => {
      show(this, e);
      e.preventDefault();
      this.emit('show');
    });
    this.selectors = {};
    this.items = [];
  }

  divider() {
    addDivider(this);
    return this;
  }

  add(itemBuilder) {
    const item = itemBuilder(new ContextMenuItem());

    if (!item) {
      return this;
    }

    addItem(this, item);
    this.items.push(item);

    return this;
  }

  get visible() {
    if (!this.elements || !this.elements.menu) {
      return false;
    }

    return this.elements.menu.offsetWidth > 0;
  }

  close() {
    const isVisible = this.visible;
    if (isVisible) {
      this.elements.dropdown.style.display = 'none';
    }
    return isVisible;
  }

  // Determines whether or not an event could have originated from the context
  // menu.
  isEventSource(e) {
    if (!e) return false;

    const path = e.composedPath();

    if (!path || !this.elements) {
      return false;
    }

    return path.includes(this.elements.menu);
  }
}

const AVAILABLE_THEMES = ['light', 'dark'];
const DEFAULT_THEME = 'light';

const defaultOptions = {
  contextMenu: false,
  pan: {
    momentum: true, // if true, enables momentum on panning
    boundary: {
      contain: null, // selector for contained element
      overlap: 300, // px
    },
    tweenTime: 250, // ms
  },
  theme: DEFAULT_THEME,
  zoom: {
    controls: false, // display zoom controls (+ / - buttons)
    step: 0.1, // zoom step when clicking a button in the interface
    minRatio: 0.1, // minimum zoom scale
    maxRatio: 1.0, // maximum zoom scale
    requireActive: false, // whether or not the user must interact with the element before zooming
  },
};

const clamp$1 = (x, min, max) => Math.min(Math.max(x, min), max);

// TODO
// Retire this class in favor of a Vue component
class Container extends EventSource {
  constructor(parent, options = {}, element = null, contentElement = null) {
    super();

    this.options = deepmerge(defaultOptions, options);
    this.scaleTarget = false;

    let { theme } = this.options;

    if (AVAILABLE_THEMES.indexOf(theme) === -1) {
      theme = DEFAULT_THEME;
    }

    this.element = element;
    if (!element) {
      this.element = document.createElement('div');
      this.element.className = `appmap appmap--theme-${theme}`;
    }

    this.contentElement = contentElement || document.createElement('div');
    this.contentElement.className = 'appmap__content';
    this.contentElement.containerController = this;

    if (!this.contentElement.parentElement) {
      this.element.appendChild(this.contentElement);
    }

    let parentElement = element;
    if (parent) {
      parentElement = select(parent).node();
      parentElement.appendChild(this.element);
    }

    if (this.options.zoom) {
      if (this.options.zoom.controls) {
        this.zoomController = new ContainerZoom(this, this.options.zoom).on('zoom', (k) => {
          const { minRatio, maxRatio } = this.options.zoom;
          const scaleLevel = (maxRatio - minRatio) * k + minRatio;

          if (this.scaleTarget && this.scaleTarget.x && this.scaleTarget.y) {
            this.scaleToAndTranslate(scaleLevel, this.scaleTarget.x, this.scaleTarget.y);
          } else {
            this.scaleTo(scaleLevel);
          }
          this.active = true;
        });
      }

      this.zoom = zoom()
        .scaleExtent([this.options.zoom.minRatio, this.options.zoom.maxRatio])
        .interpolate(interpolate)
        .filter((d3Event) => {
          if (d3Event.type === 'wheel') {
            return this.active || !this.options.zoom.requireActive;
          }

          // Mutating state in a filter is not so great here. So far I've been
          // unsuccessful at binding a 'start' handler to do this. I'm all for
          // moving this mutation somewhere more appropriate if someone would
          // like to take the time to do so. -DB
          this.active = true;

          return true;
        })
        .on('zoom', (d3Event) => {
          const { transform } = d3Event;

          const { offsetHeight, offsetWidth } = parentElement;

          transform.x = clamp$1(
            transform.x,
            (this.options.pan.boundary.overlap - this.contentElement.offsetWidth) * transform.k,
            offsetWidth - this.options.pan.boundary.overlap * transform.k
          );

          transform.y = clamp$1(
            transform.y,
            (this.options.pan.boundary.overlap - this.contentElement.offsetHeight) * transform.k,
            offsetHeight - this.options.pan.boundary.overlap * transform.k
          );

          this.contentElement.style.transform = `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`;
          this.contentElement.style.transformOrigin = '0 0';

          this.emit('move', transform);
        });

      if (this.options.pan.momentum) {
        momentum(this.zoom, select(this.element));
      }

      select(this.element).call(this.zoom).on('dblclick.zoom', null);
    }
  }

  setContextMenu(componentController) {
    if (this.options.contextMenu === false || typeof this.options.contextMenu !== 'function') {
      return;
    }

    this.contextMenu = new ContextMenu(this.element);

    const contextMenuItems = this.options.contextMenu(componentController);

    contextMenuItems.forEach((item) => this.contextMenu.add(item));
  }

  fitViewport(targetElement) {
    const targetHeight = targetElement.offsetHeight;
    const targetWidth = targetElement.offsetWidth;
    const { clientWidth, clientHeight } = this.element.parentNode;
    const { minRatio, maxRatio } = this.options.zoom;
    const desiredRatio = Math.min(clientHeight / targetHeight, clientWidth / targetWidth);
    const initialScale = Math.max(Math.min(Math.max(desiredRatio, minRatio), maxRatio), 0.8);
    const transformMatrix = zoomIdentity
      .translate(
        (clientWidth - targetWidth * initialScale) * 0.5,
        (clientHeight - targetHeight * initialScale) * 0.5
      )
      .scale(initialScale);

    this.transform = transformMatrix;
  }

  centerX(verticalPadding = 0) {
    if (!this.element || !this.element.parentNode || !this.contentElement) {
      return;
    }

    const { offsetWidth: targetWidth } = this.contentElement;
    const { clientWidth } = this.element.parentNode;

    const transformMatrix = zoomIdentity.translate(
      clientWidth * 0.5 - targetWidth * 0.5,
      verticalPadding
    );

    this.transform = transformMatrix;
  }

  translateTo(x, y, target = null) {
    select(this.element)
      .transition()
      .duration(this.options.pan.tweenTime)
      .call(this.zoom.translateTo, x, y, target);
  }

  translateBy(x, y) {
    select(this.element)
      .transition()
      .duration(this.options.pan.tweenTime)
      .call(this.zoom.translateBy, x, y);
  }

  scaleTo(k) {
    select(this.element).transition().duration(100).call(this.zoom.scaleTo, k);
  }

  scaleToAndTranslate(k, x, y) {
    select(this.element)
      .transition()
      .duration(100)
      .call(this.zoom.scaleTo, k)
      .transition()
      .duration(this.options.pan.tweenTime)
      .call(this.zoom.translateTo, x, y, null);
  }

  get transform() {
    return zoomTransform(this.element);
  }

  set transform(transform) {
    select(this.element).call(this.zoom.transform, transform);
  }
}

function generateHash() {
  return Math.random().toString(16).slice(2, 10);
}

function getAnimationStep(from, to, percent) {
  return percent < 1 ? from + (to - from) * percent : to;
}

function createSVGElement(tagName, className = null) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tagName);

  if (className) {
    el.setAttribute('class', className);
  }

  return el;
}

// transform 'dagre' points into simple array which will be passed to 'd3-line' helper
function transformPoints(points) {
  const result = [];

  points.forEach((p) => {
    result.push([p.x, p.y]);
  });

  return result;
}

// when we need to transition between 2 edges with different points length - fill smaller array with copies of it's first point
function normalizePoints(points1, points2) {
  if (points1.length === points2.length) {
    return;
  }

  const smaller = points1.length < points2.length ? points1 : points2;
  const bigger = points2 === smaller ? points1 : points2;

  for (let i = 0, len = bigger.length - smaller.length; i < len; i += 1) {
    smaller.splice(0, 0, smaller[0]);
  }
}

function findTraversableNodesAndEdges(graph, id) {
  const visitedNodes = new Set();
  const visitedEdges = new Set();
  const queue = [id];

  // traverse left from id
  while (queue.length > 0) {
    const currentId = queue.pop();
    if (!visitedNodes.has(currentId)) {
      graph.inEdges(currentId).forEach((e) => {
        visitedEdges.add(e);
        queue.push(e.v);
      });

      visitedNodes.add(currentId);
    }
  }

  // traverse right from id
  queue.push(id);
  visitedNodes.delete(id);

  while (queue.length > 0) {
    const currentId = queue.pop();
    if (!visitedNodes.has(currentId)) {
      graph.outEdges(currentId).forEach((e) => {
        visitedEdges.add(e);
        queue.push(e.w);
      });

      visitedNodes.add(currentId);
    }
  }
  return [visitedNodes, visitedEdges];
}

function Rect(width, height) {
  const rect = createSVGElement('rect');
  rect.setAttribute('rx', 0);
  rect.setAttribute('ry', 0);
  rect.setAttribute('x', -(width / 2));
  rect.setAttribute('y', -(height / 2));
  rect.setAttribute('width', width);
  rect.setAttribute('height', height);
  return rect;
}

const iconSize = 14;

function createSVGIcon(icon) {
  const svg = createSVGElement('svg', `label__icon label__icon--${icon}`);
  svg.setAttribute('viewBox', '0 0 512 512');
  svg.setAttribute('width', `${iconSize}px`);
  svg.setAttribute('height', `${iconSize}px`);

  const rect = createSVGElement('rect');
  rect.setAttribute('x', 0);
  rect.setAttribute('y', 0);
  rect.setAttribute('width', '100%');
  rect.setAttribute('height', '100%');
  rect.setAttribute('fill', 'transparent');
  svg.appendChild(rect);

  switch (icon) {
    case 'expand': {
      const path1 = createSVGElement('path');
      path1.setAttribute(
        'd',
        'm453.332031 512h-394.664062c-32.363281 0-58.667969-26.304688-58.667969-58.667969v-394.664062c0-32.363281 26.304688-58.667969 58.667969-58.667969h394.664062c32.363281 0 58.667969 26.304688 58.667969 58.667969v394.664062c0 32.363281-26.304688 58.667969-58.667969 58.667969zm-394.664062-480c-14.699219 0-26.667969 11.96875-26.667969 26.667969v394.664062c0 14.699219 11.96875 26.667969 26.667969 26.667969h394.664062c14.699219 0 26.667969-11.96875 26.667969-26.667969v-394.664062c0-14.699219-11.96875-26.667969-26.667969-26.667969zm0 0'
      );
      svg.appendChild(path1);

      const path2 = createSVGElement('path');
      path2.setAttribute(
        'd',
        'm368 272h-224c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h224c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0'
      );
      svg.appendChild(path2);

      const path3 = createSVGElement('path');
      path3.setAttribute(
        'd',
        'm256 384c-8.832031 0-16-7.167969-16-16v-224c0-8.832031 7.167969-16 16-16s16 7.167969 16 16v224c0 8.832031-7.167969 16-16 16zm0 0'
      );
      svg.appendChild(path3);
      break;
    }
    case 'collapse': {
      const path1 = createSVGElement('path');
      path1.setAttribute(
        'd',
        'm453.332031 512h-394.664062c-32.363281 0-58.667969-26.304688-58.667969-58.667969v-394.664062c0-32.363281 26.304688-58.667969 58.667969-58.667969h394.664062c32.363281 0 58.667969 26.304688 58.667969 58.667969v394.664062c0 32.363281-26.304688 58.667969-58.667969 58.667969zm-394.664062-480c-14.699219 0-26.667969 11.96875-26.667969 26.667969v394.664062c0 14.699219 11.96875 26.667969 26.667969 26.667969h394.664062c14.699219 0 26.667969-11.96875 26.667969-26.667969v-394.664062c0-14.699219-11.96875-26.667969-26.667969-26.667969zm0 0'
      );
      svg.appendChild(path1);

      const path2 = createSVGElement('path');
      path2.setAttribute(
        'd',
        'm368 272h-224c-8.832031 0-16-7.167969-16-16s7.167969-16 16-16h224c8.832031 0 16 7.167969 16 16s-7.167969 16-16 16zm0 0'
      );
      svg.appendChild(path2);

      svg.setAttribute('y', '5px');
      break;
    }
  }

  return svg;
}

class LabelGroup {
  constructor(label, icon = false, isHidden = false) {
    this.element = createSVGElement('g', 'label');

    if (icon) {
      const iconEl = createSVGIcon(icon);
      this.element.appendChild(iconEl);
    }

    if (isHidden) {
      this.element.setAttribute('opacity', 0);
    }

    this.textElement = createSVGElement('text');
    this.tspanElement = createSVGElement('tspan');
    this.tspanElement.setAttribute('space', 'preserve');
    this.tspanElement.setAttribute('dy', '1em');
    this.tspanElement.setAttribute('x', 1 + (icon ? iconSize + 6 : 0));
    this.tspanElement.textContent = label;
    this.textElement.appendChild(this.tspanElement);
    this.element.appendChild(this.textElement);
  }

  getBBox() {
    return this.element.getBBox();
  }

  show() {
    this.element.setAttribute('opacity', 1);
    return this;
  }

  hide() {
    this.element.setAttribute('opacity', 0);
    return this;
  }

  cutToWidth(width) {
    const textEl = this.textElement;
    const textSpanEl = this.tspanElement;
    let string = textEl.textContent;

    const title = createSVGElement('title');
    title.textContent = string;
    this.textElement.appendChild(title);

    while (textEl.getComputedTextLength() >= width && string.length > 0) {
      if (string.includes('/')) {
        const packagePath = string.split('/');
        packagePath.shift();
        string = packagePath.join('/');
        textSpanEl.textContent = `.../${string}`;
      } else {
        string = string.slice(0, -1);
        textSpanEl.textContent = `${string}...`;
      }
    }

    return this;
  }
}

const PADDING = 4;
const LABEL_RESERVE_SPACE = PADDING * 2 + 30;

function setElementPosition$1(nodeGroup, x, y) {
  /* eslint-disable no-param-reassign */
  nodeGroup.element.setAttribute('transform', `translate(${x},${y})`);
  nodeGroup.position = { x, y };
  /* eslint-enable no-param-reassign */
}

class ClusterGroup {
  constructor(node) {
    const classes = ['cluster'];
    if (node.children > 1) {
      classes.push(`cluster--${node.class}`, 'cluster--bordered');
    }

    this.element = createSVGElement('g', classes.join(' '));
    this.element.dataset.id = node.id;
    this.element.dataset.type = node.class;

    setElementPosition$1(this, node.x, node.y);

    const rect = Rect(node.width, node.height);
    this.element.appendChild(rect);

    this.label = new LabelGroup(node.label, 'collapse');
    this.label.hide();
    this.moveLabel(node.width, node.height);
    this.element.appendChild(this.label.element);

    requestAnimationFrame(() => {
      this.label.cutToWidth(node.width - LABEL_RESERVE_SPACE);
      this.label.show();
    });
  }

  move(x, y) {
    if (this.animationOptions && this.animationOptions.enable) {
      const initialX = this.position.x;
      const initialY = this.position.y;
      const start = +new Date();
      const { duration } = this.animationOptions;

      const tick = () => {
        const now = +new Date();
        const percent = (now - start) / duration;

        const newX = getAnimationStep(initialX, x, percent);
        const newY = getAnimationStep(initialY, y, percent);

        setElementPosition$1(this, newX, newY);

        if (percent < 1) {
          window.requestAnimationFrame(tick);
        } else {
          setElementPosition$1(this, x, y);
        }
      };
      tick();
    } else {
      setElementPosition$1(this, x, y);
    }
  }

  resize(width, height) {
    const rect = this.element.querySelector('rect');
    rect.setAttribute('x', -(width / 2));
    rect.setAttribute('y', -(height / 2));
    rect.setAttribute('width', width);
    rect.setAttribute('height', height);

    this.moveLabel(width, height);
    this.label
      .hide()
      .cutToWidth(width - LABEL_RESERVE_SPACE)
      .show();
  }

  moveLabel(width, height) {
    const dX = -width * 0.5 + PADDING;
    const dY = -height * 0.5;
    this.label.element.style.transform = `translate(${dX}px, ${dY}px)`;
  }

  remove() {
    const { element } = this;
    element.parentNode.removeChild(element);
  }
}

function Cylinder(width, height) {
  const path = createSVGElement('path');

  const rx = width / 2;
  const ry = rx / (2.5 + width / 50);

  const shape = `M 0,${ry} a ${rx},${ry} 0,0,0 ${width} 0 a ${rx},${ry} 0,0,0 ${-width} 0 l 0,${height} a ${rx},${ry} 0,0,0 ${width} 0 l 0, ${-height}`;

  path.setAttribute('d', shape);
  path.setAttribute('transform', `translate(${-width / 2}, ${-(height / 2 + ry)})`);

  return path;
}

function Parallelogram(width, height) {
  const polygon = createSVGElement('polygon');
  const w = width;
  const h = height;
  const points = [
    { x: (-2 * h) / 6, y: 0 },
    { x: w - h / 6, y: 0 },
    { x: w + (2 * h) / 6, y: -h },
    { x: h / 6, y: -h },
  ];

  polygon.setAttribute('points', points.map((d) => `${d.x},${d.y}`).join());
  polygon.setAttribute('transform', `translate(${-w * 0.5}, ${h * 0.5})`);

  return polygon;
}

function setElementPosition(nodeGroup, x, y) {
  /* eslint-disable no-param-reassign */
  nodeGroup.element.setAttribute('transform', `translate(${x},${y})`);
  nodeGroup.position = { x, y };
  /* eslint-enable no-param-reassign */
}

class NodeGroup {
  constructor(node, animationOptions = {}) {
    this.element = createSVGElement('g', `node ${node.class}`);
    this.element.dataset.id = node.id;
    this.element.dataset.type = node.codeObject.type;
    this.element.setAttribute('opacity', 0);

    setElementPosition(this, node.x, node.y);

    this.animationOptions = animationOptions;

    let shape;

    switch (node.shape) {
      case 'http':
        shape = Parallelogram(node.width, node.height);
        break;
      case 'database':
        shape = Cylinder(node.width, node.height);
        break;
      default:
        shape = Rect(node.width, node.height);
        break;
    }

    this.element.appendChild(shape);

    const labelGroup = new LabelGroup(
      node.label,
      ['http', 'package'].includes(node.class) ? 'expand' : false
    );
    labelGroup.element.setAttribute(
      'transform',
      `translate(-${node.labelWidth / 2},-${node.labelHeight / 2})`
    );
    this.element.appendChild(labelGroup.element);
  }

  show() {
    if (this.animationOptions && this.animationOptions.enable) {
      const start = +new Date();
      const { duration } = this.animationOptions;

      const tick = () => {
        const now = +new Date();
        const percent = (now - start) / duration;

        this.element.setAttribute('opacity', percent);

        if (percent < 1) {
          window.requestAnimationFrame(tick);
        } else {
          this.element.setAttribute('opacity', 1);
        }
      };
      tick();
    } else {
      this.element.setAttribute('opacity', 1);
    }
  }

  move(x, y) {
    if (this.animationOptions && this.animationOptions.enable) {
      const initialX = this.position.x;
      const initialY = this.position.y;
      const start = +new Date();
      const { duration } = this.animationOptions;

      const tick = () => {
        const now = +new Date();
        const percent = (now - start) / duration;

        const newX = getAnimationStep(initialX, x, percent);
        const newY = getAnimationStep(initialY, y, percent);

        setElementPosition(this, newX, newY);

        if (percent < 1) {
          window.requestAnimationFrame(tick);
        } else {
          setElementPosition(this, x, y);
        }
      };
      tick();
    } else {
      setElementPosition(this, x, y);
    }
  }

  remove() {
    const { element } = this;
    element.parentNode.removeChild(element);
  }
}

const lineGenerator = line().curve(curveBasis);

class Path {
  constructor(points) {
    this.element = createSVGElement('path');
    this.element.setAttribute('d', lineGenerator(points));
  }

  setPoints(points) {
    this.element.setAttribute('d', lineGenerator(points));
  }
}

function Marker() {
  const marker = createSVGElement('marker');
  marker.setAttribute('viewBox', '0 0 10 10');
  marker.setAttribute('refX', '10');
  marker.setAttribute('refY', '5');
  marker.setAttribute('markerUnits', 'strokeWidth');
  marker.setAttribute('markerWidth', '4');
  marker.setAttribute('markerHeight', '4');
  marker.setAttribute('orient', 'auto');

  const path = createSVGElement('path');
  path.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
  marker.appendChild(path);

  return marker;
}

function setPathPoints(edgeGroup, points) {
  /* eslint-disable no-param-reassign */
  edgeGroup.points = points;
  edgeGroup.path.setPoints(points);
  /* eslint-enable no-param-reassign */
}

class EdgeGroup {
  constructor(points, animationOptions = {}) {
    this.animationOptions = animationOptions;
    this.points = transformPoints(points);

    this.element = createSVGElement('g', 'edgePath');
    this.element.setAttribute('opacity', 0);
    this.path = new Path(this.points);

    const defs = createSVGElement('defs');
    const marker = Marker();

    const arrowId = `arrowhead${generateHash()}`;
    marker.setAttribute('id', arrowId);
    this.path.element.setAttribute('marker-end', `url(#${arrowId})`);

    defs.appendChild(marker);

    this.element.appendChild(this.path.element);
    this.element.appendChild(defs);
  }

  show() {
    if (this.animationOptions && this.animationOptions.enable) {
      const start = +new Date();
      const { duration } = this.animationOptions;

      const tick = () => {
        const now = +new Date();
        const percent = (now - start) / duration;

        this.element.setAttribute('opacity', percent);

        if (percent < 1) {
          window.requestAnimationFrame(tick);
        } else {
          this.element.setAttribute('opacity', 1);
        }
      };
      tick();
    } else {
      this.element.setAttribute('opacity', 1);
    }
  }

  move(points) {
    if (this.animationOptions && this.animationOptions.enable) {
      const startPoints = Array.from(this.points);
      const endPoints = transformPoints(points);
      normalizePoints(startPoints, endPoints);

      const start = +new Date();
      const { duration } = this.animationOptions;

      const tick = () => {
        const now = +new Date();
        const percent = (now - start) / duration;

        const currentPoints = [];

        startPoints.forEach((item, index) => {
          currentPoints.push([
            getAnimationStep(item[0], endPoints[index][0], percent),
            getAnimationStep(item[1], endPoints[index][1], percent),
          ]);
        });

        setPathPoints(this, currentPoints);

        if (percent < 1) {
          window.requestAnimationFrame(tick);
        } else {
          setPathPoints(this, endPoints);
        }
      };
      tick();
    } else {
      setPathPoints(this, points);
    }
  }

  remove() {
    const { element } = this;
    element.parentNode.removeChild(element);
  }
}

const NODE_PADDING_HORIZONTAL = 15;
const NODE_PADDING_VERTICAL = 10;

const DEFAULT_OPTIONS = {
  animation: {
    enable: true,
    duration: 300,
  },
};

class Graph {
  constructor(element, options = {}) {
    this.element = element;
    this.options = deepmerge(DEFAULT_OPTIONS, options);

    this.outputGroup = createSVGElement('g', 'output');
    this.edgesGroup = createSVGElement('g', 'edgePaths');
    this.clustersGroup = createSVGElement('g', 'clusters');
    this.nodesGroup = createSVGElement('g', 'nodes');

    this.outputGroup.appendChild(this.clustersGroup);
    this.outputGroup.appendChild(this.edgesGroup);
    this.outputGroup.appendChild(this.nodesGroup);

    this.element.innerHTML = '';
    this.element.appendChild(this.outputGroup);

    this.graph = new dagre.graphlib.Graph({ compound: true })
      .setGraph({ rankdir: 'LR' })
      .setDefaultEdgeLabel(() => ({}));
  }

  setNodeFromCodeObject(codeObject, parentId = null) {
    let label = codeObject.prettyName;

    if (codeObject.type === CodeObjectType.PACKAGE || codeObject.type === CodeObjectType.HTTP) {
      const numChildren = codeObject.childLeafs().length;

      label += ` (${numChildren})`;
    }

    const node = {
      codeObject,
      label,
      id: codeObject.id,
      class: codeObject.type,
      shape: codeObject.type,
    };
    this.setNode(node, parentId);
  }

  setNode(data, parentId = null) {
    const node = { ...data };
    if (this.graph.node(node.id)) {
      return;
    }

    if (node.type !== 'cluster') {
      // create dummy <g class="node"> with label to determine label width
      const dummyNodeGroup = createSVGElement('g', 'node');
      const labelGroup = new LabelGroup(
        node.label,
        ['http', 'package'].includes(node.class) ? 'expand' : false,
        true
      );
      dummyNodeGroup.appendChild(labelGroup.element);
      this.nodesGroup.appendChild(dummyNodeGroup);
      const labelBBox = labelGroup.getBBox();
      this.nodesGroup.removeChild(dummyNodeGroup);

      node.labelWidth = labelBBox.width;
      node.labelHeight = labelBBox.height;
      node.width = labelBBox.width + NODE_PADDING_HORIZONTAL * 2;
      node.height = labelBBox.height + NODE_PADDING_VERTICAL * 2;
    }

    this.graph.setNode(node.id, node);

    if (parentId) {
      this.graph.setParent(node.id, parentId);
    }
  }

  removeNode(id) {
    const edges = this.graph.nodeEdges(id);
    const node = this.graph.node(id);

    if (edges) {
      edges.forEach(({ v, w }) => {
        const edge = this.graph.edge(v, w);
        if (edge.group) {
          edge.group.remove();
        }
        this.graph.removeEdge(v, w);
      });
    }

    node.group.remove();
    this.graph.removeNode(id);
  }

  setEdge(codeObjectFrom, codeObjectTo) {
    const idFrom = codeObjectFrom.id;
    const idTo = codeObjectTo.id;

    if (codeObjectFrom === codeObjectTo || this.graph.edge(idFrom, idTo)) {
      return;
    }

    if (!this.graph.node(idTo) || this.graph.children(idTo).length) {
      return;
    }

    if (!this.graph.node(idFrom) || this.graph.children(idFrom).length) {
      return;
    }

    this.graph.setEdge(idFrom, idTo, {
      codeObjectTo,
      codeObjectFrom,
    });
  }

  render() {
    dagre.layout(this.graph);

    this.graph.nodes().forEach((id) => {
      const node = this.graph.node(id);

      if (node.group) {
        node.group.move(node.x, node.y);

        if (node.type === 'cluster') {
          node.group.resize(node.width, node.height);
        }

        return;
      }

      if (node.type === 'cluster') {
        const clusterGroup = new ClusterGroup(node);
        node.group = clusterGroup;
        node.element = clusterGroup.element;
        this.clustersGroup.appendChild(clusterGroup.element);
      } else {
        const nodeGroup = new NodeGroup(node, this.options.animation);

        node.group = nodeGroup;
        node.element = nodeGroup.element;

        this.nodesGroup.appendChild(nodeGroup.element);

        nodeGroup.show();
      }
    });

    this.graph.edges().forEach(({ v, w }) => {
      const edge = this.graph.edge(v, w);

      if (edge.group) {
        edge.group.move(edge.points);
      } else {
        const edgeGroup = new EdgeGroup(edge.points, this.options.animation);
        const fromNode = this.graph.node(v);
        const toNode = this.graph.node(w);

        edgeGroup.element.dataset.from = v;
        edgeGroup.element.dataset.fromType = fromNode.codeObject.type;

        edgeGroup.element.dataset.to = w;
        edgeGroup.element.dataset.toType = toNode.codeObject.type;

        edge.group = edgeGroup;
        edge.element = edgeGroup.element;

        this.edgesGroup.appendChild(edgeGroup.element);

        edgeGroup.show();
      }
    });

    this.element.setAttribute('width', this.graph.graph().width);
    this.element.setAttribute('height', this.graph.graph().height);
  }

  clearHighlights() {
    this.outputGroup.querySelectorAll('.highlight,.highlight--inbound').forEach((el) => {
      el.classList.remove('highlight');
      el.classList.remove('highlight--inbound');
    });
  }

  highlightNode(id) {
    const highlightedNode = this.graph.node(id);
    if (!highlightedNode) {
      return null;
    }

    const children = this.graph.children(id);
    if (highlightedNode.type === 'cluster') {
      children.forEach((childId) => this.highlightNode(childId));
    }

    if (highlightedNode.element.classList.contains('dim')) {
      return null;
    }

    highlightedNode.element.classList.add('highlight');

    this.graph.nodeEdges(id).forEach((e) => {
      const edge = this.graph.edge(e).element;
      edge.classList.add('highlight');

      if (id === e.w) {
        edge.classList.add('highlight--inbound');
      }

      // Render highlighted connections above non-highlighted connections
      if (!edge.classList.contains('dim')) {
        const parent = edge.parentNode;
        parent.removeChild(edge);
        parent.appendChild(edge);
      }
    });

    return highlightedNode.codeObject;
  }

  clearFocus() {
    this.outputGroup.querySelectorAll('.dim').forEach((el) => {
      el.classList.remove('dim');
    });
  }

  focus(id) {
    const [visitedNodes, visitedEdges] = findTraversableNodesAndEdges(this.graph, id);

    this.graph.nodes().forEach((nodeId) => {
      if (visitedNodes.has(nodeId)) {
        return;
      }

      const node = this.graph.node(nodeId);
      if (node.type !== 'cluster') {
        node.element.classList.add('dim');
      }
    });

    this.graph.edges().forEach((edgeId) => {
      if (visitedEdges.has(edgeId)) {
        return;
      }

      const edge = this.graph.edge(edgeId).element;
      edge.classList.add('dim');

      const parent = edge.parentNode;
      parent.removeChild(edge);
      parent.insertAdjacentElement('afterbegin', edge);
    });
  }

  expand(codeObject, children) {
    const { id } = codeObject;

    this.graph.edges().forEach(({ v, w }) => {
      if (v === id || w === id) {
        const edge = this.graph.edge(v, w);
        if (edge.group) {
          edge.group.remove();
          this.graph.removeEdge(v, w);
        }
      }
    });

    this.removeNode(id);

    const clusterNode = {
      id,
      type: 'cluster',
      label: codeObject.id,
      children: children.length,
      class: codeObject.type,
      codeObject,
    };

    this.setNode(clusterNode);
    children.forEach((obj) => this.setNodeFromCodeObject(obj, id));
  }

  collapse() {
    this.render();
  }

  getNodesBox(nodes) {
    const nodesBox = {
      top: [],
      left: [],
      right: [],
      bottom: [],
      x: [],
      y: [],
    };

    nodes.forEach((id) => {
      const node = this.graph.node(id);
      if (!node) {
        return;
      }

      const nodeBox = node.element.getBoundingClientRect() || node.element.getBBox();
      nodesBox.top.push(nodeBox.top);
      nodesBox.left.push(nodeBox.left);
      nodesBox.right.push(nodeBox.right);
      nodesBox.bottom.push(nodeBox.bottom);
      nodesBox.x.push(node.x - nodeBox.width / 2);
      nodesBox.y.push(node.y - nodeBox.height / 2);
    });

    nodesBox.top = Math.min(...nodesBox.top);
    nodesBox.left = Math.min(...nodesBox.left);
    nodesBox.right = Math.max(...nodesBox.right);
    nodesBox.bottom = Math.max(...nodesBox.bottom);
    nodesBox.offsetTop = Math.min(...nodesBox.y);
    nodesBox.offsetLeft = Math.min(...nodesBox.x);

    nodesBox.width = nodesBox.right - nodesBox.left;
    nodesBox.height = nodesBox.bottom - nodesBox.top;

    return nodesBox;
  }

  scrollToNodes(container, nodes) {
    const nodesBox = this.getNodesBox(nodes);
    const containerBox = container.getBoundingClientRect();

    if (Geometry.contains(containerBox, nodesBox)) {
      return false;
    }

    const xRatio = containerBox.width / nodesBox.width;
    const yRatio = containerBox.height / nodesBox.height;
    const scale = xRatio > 1 && yRatio > 1 ? 1 : Math.min(xRatio, yRatio) - 0.01;

    return {
      scale,
      x: nodesBox.width / 2 + nodesBox.offsetLeft,
      y: nodesBox.height / 2 + nodesBox.offsetTop,
    };
  }

  edge(to, from) {
    return this.graph.edge(to, from);
  }

  node(id) {
    return this.graph.node(id);
  }
}

const DEFAULT_TARGET_COUNT = 1;

// Types which have render support within the diagram
const renderableTypes = [
  CodeObjectType.DATABASE,
  CodeObjectType.HTTP,
  CodeObjectType.EXTERNAL_SERVICE,
  CodeObjectType.ROUTE,
  CodeObjectType.PACKAGE,
  CodeObjectType.CLASS,
];

function codeObjectFromElement(element, classMap) {
  const { id, type } = element.dataset;
  return classMap.codeObjects.find((obj) => obj.id === id && obj.type === type);
}

function bindEvents(componentDiagram, classMap) {
  const svg = componentDiagram.element.node();

  svg.addEventListener('click', (event) => {
    const expandIcon = getEventTarget(event.target, svg, 'g.label .label__icon--expand');
    if (!expandIcon) {
      return;
    }

    event.stopImmediatePropagation();

    const codeObject = codeObjectFromElement(expandIcon.closest('.node'), classMap);
    componentDiagram.expand(codeObject);
  });

  svg.addEventListener('click', (event) => {
    const collapseIcon = getEventTarget(event.target, svg, 'g.label .label__icon--collapse');
    if (!collapseIcon) {
      return;
    }

    event.stopImmediatePropagation();

    const codeObject = codeObjectFromElement(collapseIcon.closest('.cluster'), classMap);
    componentDiagram.collapse(codeObject);
  });

  svg.addEventListener('click', (event) => {
    const node = getEventTarget(event.target, svg, 'g.nodes g.node');
    if (!node) {
      return;
    }

    event.stopPropagation();
    const codeObject = codeObjectFromElement(node, classMap);
    componentDiagram.highlight(codeObject);
    componentDiagram.emit('click', codeObject);
  });

  svg.addEventListener('dblclick', (event) => {
    const node = getEventTarget(event.target, svg, 'g.nodes g.node');
    if (!node) {
      return;
    }

    event.stopPropagation();
    componentDiagram.focus(codeObjectFromElement(node, classMap));
  });

  svg.addEventListener('click', (event) => {
    const edge = getEventTarget(event.target, svg, '.edgePath > path');
    if (!edge) {
      return;
    }

    event.stopPropagation();
    componentDiagram.clearHighlights(true);

    edge.parentNode.classList.add('highlight');
    select(svg).selectAll('.edgePath.highlight').raise();

    const { to, from } = edge.parentElement.dataset;
    const { codeObjectTo, codeObjectFrom } = componentDiagram.graph.edge(from, to);

    componentDiagram.emit('edge', { to: codeObjectTo, from: codeObjectFrom });
  });

  svg.addEventListener('click', (event) => {
    const cluster = getEventTarget(event.target, svg, 'g.cluster');
    if (!cluster) {
      return;
    }

    event.stopPropagation();
    const codeObject = codeObjectFromElement(cluster, classMap);
    componentDiagram.highlight(codeObject);
    componentDiagram.emit('click', codeObject);
  });
}

const expandableTypes = [CodeObjectType.PACKAGE, CodeObjectType.HTTP];
const COMPONENT_OPTIONS = {
  contextMenu(componentDiagram) {
    return [
      (item) =>
        item
          .text('Set as root')
          .selector('.nodes .node')
          .transform((e) => componentDiagram.graph.node(e.dataset.id).codeObject)
          .on('execute', (id) => componentDiagram.emit('makeRoot', id)),
      (item) =>
        item
          .text('Expand')
          .selector('g.node')
          .transform((e) => componentDiagram.graph.node(e.dataset.id).codeObject)
          .condition((obj) => expandableTypes.includes(obj.type))
          .on('execute', (id) => componentDiagram.expand(id)),
      (item) =>
        item
          .text('Collapse')
          .selector('g.node')
          .transform((e) => componentDiagram.graph.node(e.dataset.id).codeObject)
          .condition(
            (obj) => !expandableTypes.includes(obj.type) && obj.type !== CodeObjectType.DATABASE
          )
          .on('execute', (id) => componentDiagram.collapse(id)),
      (item) =>
        item
          .text('Collapse')
          .selector('g.cluster')
          .transform((e) => componentDiagram.graph.node(e.dataset.id).codeObject)
          .on('execute', (obj) => componentDiagram.collapse(obj)),
      (item) =>
        item.text('Reset view').on('execute', () => {
          componentDiagram.render(componentDiagram.classMap);
        }),
    ];
  },
};

// These functions clearly need an API abstraction, I'm just not sure what that is yet. -DB
function inboundEdges(...codeObjects) {
  return codeObjects
    .map((obj) =>
      obj.inboundConnections
        .map((connection) => [connection, ...connection.ancestors()])
        .flat()
        .map((connection) => ({
          from: connection,
          to: obj,
        }))
    )
    .flat()
    .filter((edge) => edge.to !== edge.from);
}

function outboundEdges(...codeObjects) {
  return codeObjects
    .map((obj) =>
      obj.outboundConnections
        .map((connection) => [connection, ...connection.ancestors()])
        .flat()
        .map((connection) => ({
          from: obj,
          to: connection,
        }))
    )
    .flat()
    .filter((edge) => edge.to !== edge.from);
}

function allEdges(...codeObjects) {
  // Grab all the connections and build edges for every ancestor of that connection.
  return [...outboundEdges(...codeObjects), ...inboundEdges(...codeObjects)];
}

class ComponentDiagram extends EventSource {
  constructor(container, options = {}) {
    super();

    const componentDiagramOptions = deepmerge(COMPONENT_OPTIONS, options);

    this.container = new Container(container, componentDiagramOptions);
    this.container.setContextMenu(this);

    this.targetCount = DEFAULT_TARGET_COUNT;
    this.element = select(this.container.contentElement)
      .append('svg')
      .attr('class', 'appmap__component-diagram');

    this.on('postrender', () => {
      this.container.fitViewport(this.container.contentElement);
    });

    this.container.element.addEventListener('click', (event) => {
      if (!event.target.classList.contains('dropdown-item')) {
        this.emit('click', null);
        this.clearHighlights();
      }

      if (this.container.contextMenu) {
        this.container.contextMenu.close();
      }
    });

    this.container.element.addEventListener('move', () => {
      if (this.container.contextMenu) {
        this.container.contextMenu.close();
      }
    });

    this.container.element.addEventListener('dblclick', () => {
      this.clearFocus();
    });
  }

  render(classMap) {
    if (!classMap) {
      return;
    }

    this.classMap = classMap;
    this.graph = new Graph(this.element.node(), {
      animation: {
        duration: 600,
      },
    });

    const codeObjects = classMap.roots
      .map((root) => root.leafs())
      .flat()
      .reduce((objects, obj) => {
        const children = obj.childLeafs();

        if (children.length === 1 && renderableTypes.includes(children[0].type)) {
          // Make sure this object isn't empty
          const eventCount = obj.allEvents.length;
          if (eventCount > 0) {
            objects.push(children[0]);
          }
        } else {
          objects.push(obj);
        }

        return objects;
      }, []);
    const edges = outboundEdges(...codeObjects);

    codeObjects.forEach((codeObject) => this.graph.setNodeFromCodeObject(codeObject));
    edges.forEach((edge) => this.graph.setEdge(edge.from, edge.to));

    this.graph.render();
    bindEvents(this, classMap);
    this.emit('postrender');
  }

  clearHighlights(noEvent = false) {
    this.graph.clearHighlights();

    this.container.scaleTarget = false;

    if (!noEvent) {
      this.emit('highlight', null);
    }
  }

  highlight(...codeObjects) {
    this.clearHighlights(true);

    const highlightedCodeObjects = codeObjects
      .filter(Boolean)
      .map((obj) => this.graph.highlightNode(obj.id ?? obj.name))
      .filter(Boolean);

    if (highlightedCodeObjects.length) {
      this.scrollTo(highlightedCodeObjects);
    }

    const hlBox = this.graph.getNodesBox(highlightedCodeObjects.map((obj) => obj.id ?? obj.name));
    this.container.scaleTarget = {
      x: hlBox.width / 2 + hlBox.offsetLeft,
      y: hlBox.height / 2 + hlBox.offsetTop,
    };

    this.emit('highlight', highlightedCodeObjects);

    return highlightedCodeObjects.length > 0;
  }

  clearFocus() {
    this.graph.clearFocus();

    this.emit('focus', null);
  }

  focus(codeObject) {
    this.graph.clearFocus();
    this.graph.focus(codeObject.id);

    this.scrollTo(codeObject);

    this.emit('focus', codeObject);
  }

  scrollTo(...codeObjects) {
    const scrollOptions = this.graph.scrollToNodes(
      this.container.element,
      codeObjects.map((obj) => obj.id)
    );

    if (scrollOptions) {
      this.container.scaleTo(scrollOptions.scale);

      setTimeout(() => {
        this.container.translateTo(scrollOptions.x, scrollOptions.y);
      }, 200);

      this.emit('scrollTo', codeObjects);
    }
  }

  expand(codeObject, scrollToSubclasses = true) {
    const nodeWasHighlighted = this.isHighlighted(codeObject);
    const children = codeObject.children.map((child) => child.leafs()).flat();

    this.graph.expand(codeObject, children);
    allEdges(...children).forEach(({ from, to }) => this.graph.setEdge(from, to));

    // HACK.
    // The child graph should really be doing this, but there's no way to add the edges before
    // rendering without bloating the API any further.
    this.graph.render();

    if (scrollToSubclasses) {
      this.scrollTo(children);
    }

    if (nodeWasHighlighted) {
      this.graph.highlightNode(codeObject.id);
    }

    this.emit('expand', codeObject);
  }

  collapse(codeObject, scrollToPackage = true) {
    const codeObjectPackage = codeObject.packageObject || codeObject.parent || codeObject;
    const nodeWasHighlighted = this.isHighlighted(codeObjectPackage);
    const { id } = codeObjectPackage;

    this.graph.removeNode(id);

    codeObjectPackage.childLeafs().forEach((child) => this.graph.removeNode(child.id));

    this.graph.setNodeFromCodeObject(codeObjectPackage);

    allEdges(codeObjectPackage).forEach(({ to, from }) => this.graph.setEdge(from, to));

    this.graph.collapse();

    if (scrollToPackage) {
      this.scrollTo(codeObjectPackage);
    }

    if (nodeWasHighlighted) {
      this.graph.highlightNode(id);
    }

    this.emit('collapse', codeObjectPackage);
  }

  hasPackage(packageId) {
    return this.currentDiagramModel.packages.has(packageId);
  }

  hasObject(codeObject) {
    return Boolean(this.graph.node(codeObject.id));
  }

  isHighlighted(codeObject) {
    const { id } = codeObject;
    const element = this.element.node().querySelector(`[data-id="${id}"]`);
    return element && element.classList.contains('highlight');
  }
}

// Returns [ r, g, b ]
function parseHexColor(hexColor) {
  const result = [];
  const match = hexColor.match(/^#([a-fA-F0-9]{3,6})/);

  if (match) {
    const hex = match[1];
    const charsPerNumber = Math.ceil(hex.length / 3);
    const numberCount = hex.length / charsPerNumber;

    for (let i = 0; i < numberCount; i += charsPerNumber) {
      const slice = hex.slice(i, i + charsPerNumber);
      let bytes = parseInt(slice, 16);

      if (charsPerNumber === 1) {
        bytes *= bytes;
      }

      result.push(bytes);
    }
  }

  const len = result.length;
  for (let i = 0; i < 3 - len; i += 1) {
    result.push(0.0);
  }

  return result;
}

// Shifts the next item off the array and coerces it to a number
function getNextNumber(arr) {
  const val = arr.shift();
  if (typeof val !== 'number') {
    return 0.0;
  }
  return val;
}

function clamp(val, min, max) {
  return Math.min(Math.max(min, val), max);
}

// Convenience class for converting and mutating colors
class Color {
  // Supports the following constructors:
  // new Color(127)               -> rgba(127, 127, 127, 1.0)
  // new Color(127, 0.5)          -> rgba(127, 127, 127, 0.5)
  // new Color(10, 60, 224)       -> rgba(10, 60, 224, 1.0)
  // new Color(10, 60, 224, 0.5)  -> rgba(10, 60, 224, 0.5)
  // new Color('#f0f')            -> rgba(255, 0, 255, 1.0)
  // new Color('#08ff00')         -> rgba(127, 255, 0, 1.0)
  // new Color('#08ff00', 0.5)    -> rgba(127, 255, 0, 1.0, 0.5)
  // new Color(color)             -> rgba(color.r, color.g, color.b, color.a)
  // new Color(color, 1.0)        -> rgba(color.r, color.g, color.b, 1.0)
  constructor(...vals) {
    Object.defineProperty(this, '$data', {
      writable: false,
      enumerable: false,
      value: {
        r: 0.0,
        g: 0.0,
        b: 0.0,
        a: 0.0,
      },
    });

    switch (vals.length) {
      case 1:
        {
          const color = vals[0];
          const colorType = typeof color;

          if (colorType === 'string') {
            const [r, g, b] = parseHexColor(color);
            this.r = r;
            this.g = g;
            this.b = b;
          } else if (colorType === 'number') {
            this.r = color;
            this.g = color;
            this.b = color;
          } else if (color instanceof Color) {
            this.r = color.r;
            this.g = color.g;
            this.b = color.b;
            this.a = color.a;
          }
        }
        break;

      case 2:
        {
          const color = vals[0];
          const colorType = typeof color;

          if (colorType === 'string') {
            const [r, g, b] = parseHexColor(color);
            this.r = r;
            this.g = g;
            this.b = b;
          } else if (colorType === 'number') {
            this.r = color;
            this.g = color;
            this.b = color;
          } else if (color instanceof Color) {
            this.r = color.r;
            this.g = color.g;
            this.b = color.b;
          }

          const alpha = vals[1];
          if (typeof alpha === 'number') {
            this.a = alpha;
          }
        }
        break;

      case 3:
        this.r = getNextNumber(vals);
        this.g = getNextNumber(vals);
        this.b = getNextNumber(vals);
        break;

      case 4:
        this.r = getNextNumber(vals);
        this.g = getNextNumber(vals);
        this.b = getNextNumber(vals);
        this.a = getNextNumber(vals);
        break;
    }

    if (this.a > 1) {
      this.a /= 0xff;
    }
  }

  set r(val) {
    this.$data.r = Math.floor(clamp(val, 0, 255));
  }

  set g(val) {
    this.$data.g = Math.floor(clamp(val, 0, 255));
  }

  set b(val) {
    this.$data.b = Math.floor(clamp(val, 0, 255));
  }

  set a(val) {
    this.$data.a = clamp(val, 0, 1);
  }

  get r() {
    return this.$data.r;
  }

  get g() {
    return this.$data.g;
  }

  get b() {
    return this.$data.b;
  }

  get a() {
    return this.$data.a;
  }

  get rgb() {
    return `rgb(${this.r}, ${this.g}, ${this.b})`;
  }

  get hex() {
    const r = this.r.toString(16).padStart(2, '0');
    const g = this.g.toString(16).padStart(2, '0');
    const b = this.b.toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  get rgba() {
    return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
  }

  static hex(...vals) {
    const color = new Color(...vals);
    return color.hex;
  }

  static rgb(...vals) {
    const color = new Color(...vals);
    return color.rgb;
  }

  static rgba(...vals) {
    const color = new Color(...vals);
    return color.rgba;
  }

  darken(val) {
    this.r *= val;
    this.g *= val;
    this.b *= val;
  }

  toString() {
    return this.hex;
  }
}

export { Color, ComponentDiagram, Container, getElementCenter, getEventTarget, getParentRelativeOffset, lazyPanToElement, nodeFullyVisible };
